/**
 * Contact detail page — GET /contacts/:id.
 * DetailHeader breadcrumb + two tabs: Overview (InfoGrid) and Notes/Metadata.
 */
import { useParams, useNavigate } from 'react-router-dom';
import { Container } from '@vnetwork/ui/layout';
import { Button, Card, CardBody, Skeleton, Tabs, TabsList, TabsTrigger, TabsContent } from '@vnetwork/ui/primitives';
import { StatusPill, type StatusTone } from '@vnetwork/ui/data';
import { Alert } from '@vnetwork/ui/feedback';
import { ArrowLeft } from 'lucide-react';
import { DetailHeader } from '@/components/detail-header';
import { InfoGrid } from '@/pages/account/components/InfoGrid';
import { useContactById } from '@/api/contacts';

function statusTone(status: string | null | undefined): StatusTone {
  switch (status) {
    case 'warm-lead': return 'warning';
    case 'sale-qualified-lead': return 'success';
    case 'cool-lead': return 'info';
    case 'un-qualified-lead': return 'danger';
    case 'UNCLASSIFIED': return 'muted';
    default: return 'neutral';
  }
}

function statusLabel(status: string | null | undefined): string {
  switch (status) {
    case 'warm-lead': return 'Warm Lead';
    case 'sale-qualified-lead': return 'SQL';
    case 'cool-lead': return 'Cool Lead';
    case 'un-qualified-lead': return 'Unqualified';
    case 'UNCLASSIFIED': return 'Unclassified';
    default: return status ?? 'Unknown';
  }
}

function fmtDate(raw: string | null | undefined): string {
  if (!raw) return '—';
  const d = new Date(raw);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function ContactDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const contactId = Number(id);

  const { data, isLoading, isError, refetch } = useContactById(contactId);
  const contact = data?.result;

  if (isError) {
    return (
      <Container size="full">
        <Alert
          tone="danger"
          title="Failed to load contact"
          actions={<Button size="sm" variant="ghost" onClick={() => refetch()}>Retry</Button>}
        />
      </Container>
    );
  }

  const s = contact?.contact_source_status;
  const assigneeNames = contact?.contact_source_assignee_name?.map((a) => a.employee_name).join(', ') || '—';

  return (
    <Container size="full">
      {isLoading ? (
        <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
          <Skeleton style={{ height: 24, maxWidth: 320 }} />
          <Skeleton style={{ height: 40, maxWidth: 480 }} />
          <Skeleton style={{ height: 200 }} />
        </div>
      ) : (
        <>
          <DetailHeader
            parent={{ label: 'Contacts', to: '/contacts' }}
            current={contact?.contact_source_name || `#${contactId}`}
            idPrefix={`#${contactId}`}
            title={contact?.contact_source_name || `Contact ${contactId}`}
            meta={
              s ? (
                <StatusPill tone={statusTone(s)}>{statusLabel(s)}</StatusPill>
              ) : undefined
            }
            actions={
              <Button
                variant="secondary"
                startIcon={<ArrowLeft size={15} />}
                onClick={() => navigate('/contacts')}
              >
                Back to list
              </Button>
            }
          />

          <Tabs defaultValue="overview" style={{ marginTop: 'var(--vn-space-4)' }}>
            <TabsList>
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="metadata">Metadata</TabsTrigger>
            </TabsList>

            <TabsContent value="overview">
              <Card style={{ marginTop: 'var(--vn-space-3)' }}>
                <CardBody>
                  <InfoGrid
                    items={[
                      { label: 'Name', value: contact?.contact_source_name },
                      { label: 'Email', value: contact?.contact_source_email },
                      { label: 'Phone', value: contact?.contact_source_phone },
                      { label: 'Company', value: contact?.contact_source_company },
                      { label: 'Position', value: contact?.contact_source_position },
                      { label: 'Industry', value: contact?.contact_source_industry },
                      { label: 'Channel', value: contact?.contact_source_channel },
                      { label: 'Status', value: s ? statusLabel(s) : '—' },
                      { label: 'Assignee', value: assigneeNames },
                      { label: 'Website', value: contact?.contact_source_website },
                      { label: 'Profile', value: contact?.contact_source_profile },
                      { label: 'Website Referrer', value: contact?.website_referrer },
                      {
                        label: 'Interested Services',
                        value: contact?.contact_source_interested_services?.join(', ') || '—',
                        full: true,
                      },
                      { label: 'Notes', value: contact?.contact_source_notes, full: true },
                      { label: 'Created At', value: fmtDate(contact?.contact_source_created_at) },
                      { label: 'Updated At', value: fmtDate(contact?.contact_source_updated_at) },
                    ]}
                  />
                </CardBody>
              </Card>
            </TabsContent>

            <TabsContent value="metadata">
              <Card style={{ marginTop: 'var(--vn-space-3)' }}>
                <CardBody>
                  <InfoGrid
                    items={[
                      { label: 'Company Size', value: contact?.company_size },
                      { label: 'Deployment Time', value: contact?.deployment_time },
                      { label: 'Specific Needs', value: contact?.specific_needs },
                      { label: 'Object ID', value: contact?.contact_source_object_id },
                      { label: 'Object Type', value: contact?.contact_source_object_type },
                      { label: 'Score', value: contact?.contact_source_score },
                      {
                        label: 'Source URL',
                        value: contact?.metadata?.source_url || '—',
                        full: true,
                      },
                      {
                        label: 'Current URL',
                        value: contact?.metadata?.currentUrl || '—',
                        full: true,
                      },
                      { label: 'Host', value: contact?.metadata?.host },
                      { label: 'Country', value: contact?.metadata?.country },
                      { label: 'Tracking', value: contact?.metadata?.tracking, full: true },
                    ]}
                  />
                </CardBody>
              </Card>
            </TabsContent>
          </Tabs>
        </>
      )}
    </Container>
  );
}
